import { Box, Button, Divider, Typography } from '@mui/material'
import { ClosingAwareModal } from '~/components/closing-aware-modal'
import { SeasonWarningsBanner } from './components/season-warnings-banner'

/**
 * Confirmation dialog for ending the season.
 * Repeats any warnings and provides a final "cannot be undone" notice.
 *
 * @param {boolean} open
 * @param {Function} onClose - Cancel handler
 * @param {Function} onConfirm - Confirm handler (the actual stub submit)
 * @param {number} year
 * @param {Object[]} tasks - For repeating the warnings
 * @param {Object|null} climateData
 * @param {boolean} isSubmitting
 */
export const EndSeasonConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  year,
  tasks,
  climateData,
  isSubmitting,
}) => (
  <ClosingAwareModal
    open={open}
    onClose={onClose}
    loading={isSubmitting}
    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <Box
      sx={{
        width: { xs: '90vw', sm: '480px' },
        bgcolor: 'background.paper',
        borderRadius: '0.5rem',
        boxShadow: 24,
        p: '1.5rem 2rem',
      }}
    >
      <Typography variant="h5">End {year} Season?</Typography>

      <SeasonWarningsBanner tasks={tasks} climateData={climateData} />

      <Divider sx={{ my: '1rem' }} />

      <Typography variant="body2" sx={{ mb: '0.5rem' }}>
        The following will be <strong>archived</strong>:
      </Typography>
      <Box component="ul" sx={{ pl: '1.25rem', mb: '1rem', '& li': { mb: '0.25rem' } }}>
        <Typography component="li" variant="body2">All active crop schedules — moved to read-only history</Typography>
        <Typography component="li" variant="body2">All pending tasks — preserved under the ended season</Typography>
        <Typography component="li" variant="body2">The {year} season record — marked as ended</Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: '0.5rem' }}>
        The following will be <strong>carried forward</strong> into {year + 1}:
      </Typography>
      <Box component="ul" sx={{ pl: '1.25rem', mb: '1rem', '& li': { mb: '0.25rem' } }}>
        <Typography component="li" variant="body2">All plots and their boundaries</Typography>
        <Typography component="li" variant="body2">Crops you marked to keep — new active schedules created</Typography>
        <Typography component="li" variant="body2">A new {year + 1} season, ready to plan</Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: '0.5rem' }}>
        <strong>Nothing is deleted.</strong> Crops, tasks, phases, and reports remain queryable by season.
        Undoing this action is not yet supported.
      </Typography>

      <Box sx={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={isSubmitting}
        >
          End {year} Season
        </Button>
      </Box>
    </Box>
  </ClosingAwareModal>
)
